import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import { User, Mail, Phone, MessageSquare } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { getGeoLocation, handleFormSubmission, submitLead } from '../services/apiService';

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    description: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoLocationData, setGeoLocationData] = useState(null);
  const [detectedCountry, setDetectedCountry] = useState('');

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeoLocation = async () => {
      const geoData = await getGeoLocation();
      if (geoData) {
        setGeoLocationData(geoData);
        // Set country code based on geolocation
        const countryCode = geoData.data?.countryCode;
        const countryName = geoData.data?.countryName;
        if (countryName) {
          setDetectedCountry(countryName);
        }
        if (countryCode) {
          const countryDialCodeMap = {
            'US': '+1', 'CA': '+1', 'RU': '+7', 'KZ': '+7', 'EG': '+20', 'ZA': '+27',
            'GR': '+30', 'NL': '+31', 'BE': '+32', 'FR': '+33', 'ES': '+34', 'HU': '+36',
            'IT': '+39', 'RO': '+40', 'CH': '+41', 'AT': '+43', 'GB': '+44', 'UK': '+44',
            'DK': '+45', 'SE': '+46', 'NO': '+47', 'PL': '+48', 'DE': '+49', 'PE': '+51',
            'MX': '+52', 'CU': '+53', 'AR': '+54', 'BR': '+55', 'CL': '+56', 'CO': '+57',
            'VE': '+58', 'MY': '+60', 'AU': '+61', 'ID': '+62', 'PH': '+63', 'NZ': '+64',
            'SG': '+65', 'TH': '+66', 'JP': '+81', 'KR': '+82', 'VN': '+84', 'CN': '+86',
            'TR': '+90', 'IN': '+91', 'PK': '+92', 'AF': '+93', 'LK': '+94', 'MM': '+95',
            'IR': '+98', 'MA': '+212', 'DZ': '+213', 'TN': '+216', 'LY': '+218', 'GM': '+220',
            'SN': '+221', 'NG': '+234', 'KE': '+254', 'TZ': '+255', 'UG': '+256', 'ZW': '+263',
            'PT': '+351', 'IE': '+353', 'IS': '+354', 'FI': '+358', 'UA': '+380', 'RS': '+381',
            'CZ': '+420', 'SK': '+421', 'HK': '+852', 'MO': '+853', 'KH': '+855', 'LA': '+856',
            'BD': '+880', 'TW': '+886', 'MV': '+960', 'LB': '+961', 'JO': '+962', 'SY': '+963',
            'IQ': '+964', 'KW': '+965', 'SA': '+966', 'YE': '+967', 'OM': '+968', 'AE': '+971',
            'IL': '+972', 'BH': '+973', 'QA': '+974', 'MN': '+976', 'NP': '+977'
          };
          const dialCode = countryDialCodeMap[countryCode] || '+1';
          setFormData(prev => ({ ...prev, countryCode: dialCode }));
        }
      }
    };
    fetchGeoLocation();
  }, []);

  const countryCodes = [
    { code: '+1', country: 'US/Canada' },
    { code: '+7', country: 'Russia/Kazakhstan' },
    { code: '+20', country: 'Egypt' },
    { code: '+27', country: 'South Africa' },
    { code: '+30', country: 'Greece' },
    { code: '+31', country: 'Netherlands' },
    { code: '+32', country: 'Belgium' },
    { code: '+33', country: 'France' },
    { code: '+34', country: 'Spain' },
    { code: '+36', country: 'Hungary' },
    { code: '+39', country: 'Italy' },
    { code: '+40', country: 'Romania' },
    { code: '+41', country: 'Switzerland' },
    { code: '+43', country: 'Austria' },
    { code: '+44', country: 'UK' },
    { code: '+45', country: 'Denmark' },
    { code: '+46', country: 'Sweden' },
    { code: '+47', country: 'Norway' },
    { code: '+48', country: 'Poland' },
    { code: '+49', country: 'Germany' },
    { code: '+51', country: 'Peru' },
    { code: '+52', country: 'Mexico' },
    { code: '+53', country: 'Cuba' },
    { code: '+54', country: 'Argentina' },
    { code: '+55', country: 'Brazil' },
    { code: '+56', country: 'Chile' },
    { code: '+57', country: 'Colombia' },
    { code: '+58', country: 'Venezuela' },
    { code: '+60', country: 'Malaysia' },
    { code: '+61', country: 'Australia' },
    { code: '+62', country: 'Indonesia' },
    { code: '+63', country: 'Philippines' },
    { code: '+64', country: 'New Zealand' },
    { code: '+65', country: 'Singapore' },
    { code: '+66', country: 'Thailand' },
    { code: '+81', country: 'Japan' },
    { code: '+82', country: 'South Korea' },
    { code: '+84', country: 'Vietnam' },
    { code: '+86', country: 'China' },
    { code: '+90', country: 'Turkey' },
    { code: '+91', country: 'India' },
    { code: '+92', country: 'Pakistan' },
    { code: '+93', country: 'Afghanistan' },
    { code: '+94', country: 'Sri Lanka' },
    { code: '+95', country: 'Myanmar' },
    { code: '+98', country: 'Iran' },
    { code: '+212', country: 'Morocco' },
    { code: '+213', country: 'Algeria' },
    { code: '+216', country: 'Tunisia' },
    { code: '+218', country: 'Libya' },
    { code: '+220', country: 'Gambia' },
    { code: '+221', country: 'Senegal' },
    { code: '+234', country: 'Nigeria' },
    { code: '+254', country: 'Kenya' },
    { code: '+255', country: 'Tanzania' },
    { code: '+256', country: 'Uganda' },
    { code: '+263', country: 'Zimbabwe' },
    { code: '+351', country: 'Portugal' },
    { code: '+353', country: 'Ireland' },
    { code: '+354', country: 'Iceland' },
    { code: '+358', country: 'Finland' },
    { code: '+380', country: 'Ukraine' },
    { code: '+381', country: 'Serbia' },
    { code: '+420', country: 'Czech Republic' },
    { code: '+421', country: 'Slovakia' },
    { code: '+852', country: 'Hong Kong' },
    { code: '+853', country: 'Macau' },
    { code: '+855', country: 'Cambodia' },
    { code: '+856', country: 'Laos' },
    { code: '+880', country: 'Bangladesh' },
    { code: '+886', country: 'Taiwan' },
    { code: '+960', country: 'Maldives' },
    { code: '+961', country: 'Lebanon' },
    { code: '+962', country: 'Jordan' },
    { code: '+963', country: 'Syria' },
    { code: '+964', country: 'Iraq' },
    { code: '+965', country: 'Kuwait' },
    { code: '+966', country: 'Saudi Arabia' },
    { code: '+967', country: 'Yemen' },
    { code: '+968', country: 'Oman' },
    { code: '+971', country: 'UAE' },
    { code: '+972', country: 'Israel' },
    { code: '+973', country: 'Bahrain' },
    { code: '+974', country: 'Qatar' },
    { code: '+976', country: 'Mongolia' },
    { code: '+977', country: 'Nepal' },
  ];

  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  // Email validation - proper format check
  const validateEmail = (email) => {
    if (!email || email.trim() === '') {
      return { isValid: false, message: 'Email address is required' };
    }
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Invalid email format. Please use format: user@example.com' };
    }
    return { isValid: true, message: '' };
  };

  // Phone validation - must be exactly 10 digits, numeric only (no alphabets allowed)
  const validatePhone = (phone) => {
    if (!phone || phone.trim() === '') {
      return { isValid: false, message: 'Phone number is required' };
    }
    
    // Check if contains any non-numeric characters
    if (/\D/.test(phone)) {
      return { isValid: false, message: 'Only numbers (0-9) are allowed. No alphabets or special characters.' };
    }
    
    // Check if exactly 10 digits (no more, no less)
    if (phone.length < 10) {
      return { isValid: false, message: `Phone number must be 10 digits. You entered ${phone.length} digit${phone.length !== 1 ? 's' : ''}.` };
    }
    
    if (phone.length > 10) {
      return { isValid: false, message: 'Phone number cannot exceed 10 digits.' };
    }
    
    return { isValid: true, message: '' };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For phone field, only allow numbers and limit to 10 digits
    if (name === 'phone') {
      const numericPhone = value.replace(/\D/g, ''); // Remove all non-numeric characters
      const limitedPhone = numericPhone.slice(0, 10); // Limit to 10 digits only
      
      setFormData({
        ...formData,
        phone: limitedPhone
      });
      
      // Clear error when user starts typing
      if (errors.phone) {
        setErrors({ ...errors, phone: '' });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
      
      // Clear error when user starts typing
      if (name === 'email' && errors.email) {
        setErrors({ ...errors, email: '' });
      }
    }

    // Validate on change
    if (name === 'email' && value) {
      const emailValidation = validateEmail(value);
      if (!emailValidation.isValid) {
        setErrors({
          ...errors,
          email: emailValidation.message
        });
      }
    }

    if (name === 'phone' && value) {
      const phoneValidation = validatePhone(value);
      if (!phoneValidation.isValid) {
        setErrors({
          ...errors,
          phone: phoneValidation.message
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate before submission
    let validationErrors = {};
    
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      validationErrors.email = emailValidation.message;
    }
    
    const phoneValidation = validatePhone(formData.phone);
    if (!phoneValidation.isValid) {
      validationErrors.phone = phoneValidation.message;
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast({
        title: "Validation Error",
        description: "Please fix the errors before submitting.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    // Submit form using centralized API service with automatic redirect
    await handleFormSubmission(
      {
        name: formData.name,
        email: formData.email,
        countryCode: formData.countryCode,
        phone: formData.phone,
        description: formData.description
      },
      geoLocationData,
      (errorMessage) => {
        // Error callback - show error message
        toast({
          title: "Submission Failed",
          description: errorMessage,
          variant: "destructive"
        });
        setIsSubmitting(false);
      },
      '', // packageTitle - empty for modal
      0   // packageAmount - 0 for modal
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden">
        <div className="grid md:grid-cols-2 gap-0">
          {/* Left Side - Image */}
          <div className="relative hidden md:block">
            <img
              src="https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d"
              alt="Professional Consultation"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#4B0082]/80 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <h3 className="text-3xl font-bold mb-3">
                Get Expert Guidance
              </h3>
              <p className="text-lg opacity-90">
                Start protecting your brand today with our professional trademark services.
              </p>
              <div className="mt-6 space-y-2">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-[#FF7F7F] rounded-full mr-3"></div>
                  <span>Free Initial Consultation</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-[#FF7F7F] rounded-full mr-3"></div>
                  <span>98% Approval Success Rate</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-[#FF7F7F] rounded-full mr-3"></div>
                  <span>24/7 Expert Support</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="p-8">
            <DialogHeader className="mb-6">
              <DialogTitle className="text-3xl font-bold text-[#4B0082]">
                Secure Your Trademark Now
              </DialogTitle>
              <DialogDescription className="text-gray-600 mt-2">
                <span className="text-[#FF7F7F] font-semibold">Limited Time: Starting at $45 + FREE Expert Consultation!</span>
                <br />
                Get your brand protected in 3 simple steps.
              </DialogDescription>
            </DialogHeader>

            {detectedCountry && (
              <div className="mb-4 p-3 bg-[#FF7F7F]/10 rounded-lg border border-[#FF7F7F]/20">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-[#4B0082]">Detected Location:</span> {detectedCountry}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  name="name"
                  placeholder="Full Name *"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="pl-10 h-12 border-gray-300 focus:border-[#FF7F7F] focus:ring-[#FF7F7F]"
                />
              </div>

              <div>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    type="email"
                    name="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`pl-10 h-12 border-gray-300 focus:border-[#FF7F7F] focus:ring-[#FF7F7F] ${errors.email ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1 flex items-start gap-1">
                    <span className="text-red-500 font-bold">⚠</span>
                    <span>{errors.email}</span>
                  </p>
                )}
                {!errors.email && formData.email && /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email) && (
                  <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                    <span>✓</span>
                    <span>Valid email address</span>
                  </p>
                )}
              </div>

              <div>
                <div className="relative flex gap-2">
                  <div className="w-32">
                    <Select
                      value={formData.countryCode}
                      onValueChange={(value) => setFormData({ ...formData, countryCode: value })}
                    >
                      <SelectTrigger className="h-12 border-gray-300 focus:border-[#FF7F7F] focus:ring-[#FF7F7F]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countryCodes.map((item) => (
                          <SelectItem key={item.code} value={item.code}>
                            {item.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number (10 digits only) *"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      maxLength={10}
                      pattern="[0-9]{10}"
                      inputMode="numeric"
                      className={`pl-10 h-12 border-gray-300 focus:border-[#FF7F7F] focus:ring-[#FF7F7F] ${errors.phone ? 'border-red-500 focus:border-red-500' : ''}`}
                    />
                  </div>
                </div>
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1 flex items-start gap-1">
                    <span className="text-red-500 font-bold">⚠</span>
                    <span>{errors.phone}</span>
                  </p>
                )}
                {!errors.phone && formData.phone && formData.phone.length === 10 && (
                  <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                    <span>✓</span>
                    <span>Valid phone number</span>
                  </p>
                )}
              </div>

              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Textarea
                  name="description"
                  placeholder="Tell us about your brand protection needs... *"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="pl-10 pt-3 border-gray-300 focus:border-[#FF7F7F] focus:ring-[#FF7F7F] resize-none"
                />
              </div>

              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#FF7F7F] hover:bg-[#FF6B6B] text-white h-12 text-lg font-semibold transition-all transform hover:scale-105"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>

              <p className="text-xs text-center text-gray-500 mt-4">
                Special Offer: <span className="font-semibold text-[#FF7F7F]">$45</span> Trademark Registration + USPTO fees
              </p>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
